/**
 * Translations.
 *
 * English and Kinyarwanda. A key with no Kinyarwanda entry falls back to the
 * English string rather than rendering blank. A cooperative officer would
 * rather read one English word than an empty button.
 *
 * Data the users typed (farmer names, villages, notes, references) is never
 * translated: it is their data, and guessing at it would corrupt records.
 * Numbers, dates and money keep one format across both languages so a printed
 * report reconciles regardless of who generated it.
 */

export type Lang = 'en' | 'rw';

export const LANGUAGES: { value: Lang; label: string; native: string }[] = [
  { value: 'en', label: 'English', native: 'English' },
  { value: 'rw', label: 'Kinyarwanda', native: 'Ikinyarwanda' },
];

type Dict = Record<string, string>;

/* -------------------------------------------------------------- English */

export const EN: Dict = {
  /* app */
  'app.name': 'Smart Agri-Ledger',
  'app.tagline': 'Farmer operations, harvests and payments',
  'app.allCoops': 'All cooperatives',
  'app.loading': 'Opening your cooperative…',
  'app.offlineNote': 'Everything works offline. Records are saved on this device and are still there when the signal is not.',

  /* navigation */
  'nav.overview': 'Overview',
  'nav.farmers': 'Farmers',
  'nav.farmers.all': 'All farmers',
  'nav.farmers.active': 'Active farmers',
  'nav.farmers.groups': 'Farmer groups',
  'nav.activities': 'Activities',
  'nav.activities.all': 'All activities',
  'nav.activities.farming': 'Farming',
  'nav.activities.harvest': 'Harvesting',
  'nav.activities.other': 'Other activities',
  'nav.production': 'Production',
  'nav.production.harvests': 'Harvests',
  'nav.production.crops': 'Crops',
  'nav.production.quality': 'Quality',
  'nav.payments': 'Payments',
  'nav.payments.pending': 'Pending',
  'nav.payments.batches': 'Payment batches',
  'nav.payments.completed': 'Completed',
  'nav.payments.outstanding': 'Outstanding',
  'nav.ledger': 'Ledger',
  'nav.ledger.farmers': 'Farmer ledgers',
  'nav.ledger.transactions': 'Transactions',
  'nav.ledger.adjustments': 'Adjustments',
  'nav.reports': 'Reports',
  'nav.settings': 'Settings',
  'nav.activity': 'Activity',

  /* common actions */
  'action.add': 'Add',
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.close': 'Close',
  'action.confirm': 'Confirm',
  'action.back': 'Back',
  'action.export': 'Export',
  'action.exportCsv': 'Export to CSV',
  'action.import': 'Import',
  'action.print': 'Print',
  'action.search': 'Search',
  'action.filters': 'Filters',
  'action.clearAll': 'Clear all',
  'action.reset': 'Reset',
  'action.signIn': 'Sign in',
  'action.signOut': 'Sign out',
  'action.recordActivity': 'Record activity',
  'action.addFarmer': 'Add farmer',
  'action.approve': 'Approve',
  'action.verify': 'Verify',
  'action.reject': 'Reject',
  'action.markPaid': 'Mark as paid',
  'action.void': 'Void',
  'action.openProfile': 'Open profile',
  'action.showResults': 'Show results',
  'action.markAllRead': 'Mark all as read',
  'action.columns': 'Choose columns',
  'action.showAllColumns': 'Show all columns',
  'action.newActivityType': 'New activity type',
  'action.createActivityType': 'Create activity type',

  /* fields */
  'field.farmer': 'Farmer',
  'field.farmerId': 'Farmer ID',
  'field.idNumber': 'ID or member number',
  'field.idNumberHelp': 'National ID, or the number your cooperative already uses for this farmer.',
  'field.farmerIdHelp': 'Leave empty and the next code in sequence is used.',
  'field.farmerIdAuto': 'Auto',
  'field.activity': 'Activity',
  'field.crop': 'Crop',
  'field.quantity': 'Quantity',
  'field.unit': 'Unit',
  'field.date': 'Date',
  'field.status': 'Status',
  'field.amount': 'Amount',
  'field.amountEarned': 'Amount earned',
  'field.paymentStatus': 'Payment status',
  'field.balance': 'Balance owed',
  'field.notes': 'Notes',
  'field.phone': 'Phone',
  'field.village': 'Village',
  'field.cell': 'Cell',
  'field.sector': 'Sector',
  'field.district': 'District',
  'field.cooperative': 'Cooperative',
  'field.season': 'Season',
  'field.firstName': 'First name',
  'field.lastName': 'Last name',
  'field.gender': 'Gender',
  'field.farmSize': 'Farm size',
  'field.plots': 'Number of plots',
  'field.primaryCrop': 'Primary crop',
  'field.grade': 'Quality grade',
  'field.price': 'Price per unit',
  'field.deductions': 'Deductions',
  'field.gross': 'Gross value',
  'field.net': 'Net to farmer',
  'field.paid': 'Paid',
  'field.advances': 'Advances',
  'field.reference': 'Reference',
  'field.method': 'Method',
  'field.reason': 'Reason',
  'field.recordedBy': 'Recorded by',
  'field.lastActivity': 'Last activity',
  'field.category': 'Category',
  'field.required': 'required',
  'field.optional': 'optional',

  /* statuses */
  'status.active': 'Active',
  'status.in_progress': 'In progress',
  'status.harvested': 'Harvested',
  'status.awaiting_payment': 'Awaiting payment',
  'status.paid': 'Paid',
  'status.disputed': 'Disputed',
  'status.inactive': 'Inactive',
  'status.planned': 'Planned',
  'status.completed': 'Completed',
  'status.verified': 'Verified',
  'status.cancelled': 'Cancelled',
  'status.recorded': 'Recorded',
  'status.rejected': 'Rejected',
  'status.pending': 'Pending',
  'status.approved': 'Approved',
  'status.processing': 'Processing',
  'status.draft': 'Draft',
  'status.pending_approval': 'Pending approval',
  'status.partially_paid': 'Partially paid',

  /* categories */
  'cat.farming': 'Farming',
  'cat.harvest': 'Harvest',
  'cat.operations': 'Farm operations',
  'cat.logistics': 'Logistics',
  'cat.financial': 'Financial',
  'cat.other': 'Other',

  'cat.farming.blurb': 'Everything that happens in the field before harvest',
  'cat.harvest.blurb': 'Collecting, grading and moving produce',
  'cat.operations.blurb': 'Inspections, testing, equipment and input distribution',
  'cat.logistics.blurb': 'Pickup, delivery, warehouse and inventory movement',
  'cat.financial.blurb': 'Anything that changes what a farmer is owed',
  'cat.other.blurb': 'Training, meetings, visits and your own activity types',

  /* roles */
  'role.super_admin': 'Super Admin',
  'role.cooperative_admin': 'Cooperative Admin',
  'role.field_officer': 'Field Officer',
  'role.accountant': 'Accountant',
  'role.viewer': 'Viewer',

  /* screens */
  'role.super_admin.desc': 'Unrestricted access, including user management and voiding ledger entries.',
  'role.cooperative_admin.desc': 'Runs the cooperative: farmers, activities, harvests, and payment approval.',
  'role.field_officer.desc': 'Records field activities and harvests. Cannot create or approve payments.',
  'role.accountant.desc': 'Owns payments, batches and the ledger. Cannot record field activity.',
  'role.viewer.desc': 'Read-only access to every screen.',
  /* payments */
  'pay.outstandingTitle': 'Outstanding balances',
  'pay.outstandingSub': 'Every farmer the cooperative still owes. Pay them the way you always do, then record it here. Each figure is folded from that farmer\u2019s ledger, so it is correct by construction.',
  'pay.payAFarmer': 'Record a payment',
  'pay.totalOutstanding': 'Total outstanding',
  'pay.acrossFarmers': 'RWF across {count} farmers',
  'pay.largestBalance': 'Largest balance',
  'pay.largestBalanceFoot': 'RWF owed to one farmer',
  'pay.inAdvanceCount': 'Farmers in advance',
  'pay.inAdvanceFoot': 'Carrying money to recover',
  'pay.allSettledTitle': 'Every account is settled',
  'pay.allSettledSub': 'No farmer is owed money and no advances are outstanding.',
  'pay.pendingTitle': 'Pending payments',
  'pay.pendingSub': 'Payments approved or waiting for approval, but not yet handed over. Recording one here does not send anything.',
  'pay.completedTitle': 'Completed payments',
  'pay.completedSub': 'Payments you have made and recorded. These are the ones posted to the farmer ledgers.',
  'pay.batchesTitle': 'Payment batches',
  'pay.batchesSub': 'Build the list of who to pay, check the amounts, export it, then record each one once the money has changed hands.',
  'pay.newBatch': 'New batch',
  'pay.batchFarmers': 'Farmers',
  'pay.batchProduce': 'Total produce',
  'pay.batchTotal': 'Total amount',
  'pay.batchPaid': 'Paid',
  'pay.batchRemaining': 'Remaining',
  /* production */
  'prod.harvestsTitle': 'Harvests',
  'prod.harvestsSub': 'Every delivery received, with what it was worth. Total value is quantity × price and is recalculated on save. It is never a typed-in number.',
  'prod.cropsTitle': 'Crops',
  'prod.cropsSub': 'What each crop produced this season, and what it was worth.',
  'prod.qualityTitle': 'Quality',
  'prod.qualitySub': 'How deliveries graded, and how much of the intake has been checked.',
  /* ledger */
  'led.farmersTitle': 'Farmer ledgers',
  'led.farmersSub': 'One account per farmer. Click a row to read the full account.',
  'led.txTitle': 'Transactions',
  'led.txSub': 'The complete journal. Nothing here can be edited or deleted. A mistake is voided, which appends a reversing entry and keeps both on the record.',
  'led.adjTitle': 'Adjustments',
  'led.adjSub': 'Corrections, voided entries and their reversals: every change anyone made to a farmer balance, with a name against it.',
  'led.entriesShown': 'Entries shown',
  'led.totalCredits': 'Total credits',
  'led.totalDebits': 'Total debits',
  'led.netPosition': 'Net position',
  'led.debit': 'Debit',
  'led.credit': 'Credit',
  'led.closing': 'Closing balance',
  /* reports & profile */
  'rep.title': 'Reports',
  'rep.sub': 'Pick a report, set a date range, then export it as CSV for Excel or print it to PDF.',
  'prof.balanceOwed': 'Balance owed',
  'prof.totalEarned': 'Total earned',
  'prof.alreadyPaid': 'Already paid',
  'prof.advances': 'Advances',
  'prof.deductions': 'Deductions',
  'prof.allFarmers': 'All farmers',
  'prof.tab.overview': 'Overview',
  'prof.tab.activities': 'Activities',
  'prof.tab.harvests': 'Harvests',
  'prof.tab.payments': 'Payments',
  'prof.tab.ledger': 'Ledger',
  'prof.tab.documents': 'Documents',
  'prof.farmDetails': 'Farm details',
  'prof.recentHistory': 'Recent history',
  'pay.markPaidTitle.one': 'Record this payment as paid?',
  'pay.approveTitle.one': 'Approve this payment?',
  'pay.recordPaidN.one': 'Record 1 as paid',
  'pay.manyFarmers.one': '1 farmer',
  'pay.manyFarmers': '{count} farmers',
  'dash.payFarmers.one': '1 farmer awaiting payment · {amount} RWF',
  'dash.verifyHarvests.one': 'Verify 1 harvest record',
  'dash.approvePayments.one': 'Approve 1 pending payment',
  'dash.deliveries.one': '1 delivery',
  'pay.acrossFarmers.one': 'RWF for 1 farmer',
  'pay.batchRecordBody.one': 'This writes {amount} for 1 farmer into their ledger, as money you have already handed over.',
  'pay.recordPaid': 'Record as paid',
  'pay.recordPaidN': 'Record {count} as paid',
  'pay.markPaidTitle': 'Record {count} payments as paid?',
  'pay.markPaidBody': 'Confirm you have already handed over {amount} to {who}. This writes the payment into their ledger; it does not send any money.',
  'pay.markPaidNote': 'Only do this once the money has actually changed hands. If you record it early, the balances will say a farmer has been paid when they have not.',
  'pay.approveTitle': 'Approve {count} payments?',
  'pay.approveBody': 'Approving clears {amount} for payout. Nothing is written to the ledger until you record the payments as paid.',
  'pay.batchRecordTitle': 'Record {code} as paid?',
  'pay.batchRecordBody': 'This writes {amount} across {count} farmers into their ledgers, as money you have already handed over.',
  'pay.exportToPay': 'Export the pay list',
  'pay.exportHint': 'Take this list to the bank, the mobile money agent, or the pay table, then come back and record what you paid.',
  'pay.notAPaymentApp': 'This app records payments, it does not make them. You pay farmers the way you always have; recording it here keeps every balance correct.',
  'pay.nothingOwed': 'Nothing owed',
  'pay.partPaid': 'Part paid',
  'pay.pending': 'Pending',
  'pay.inAdvance': 'In advance',
  'pay.paid': 'Paid',
  'pay.nothingOwedHelp': 'No credits posted yet.',
  'pay.partPaidHelp': 'Some of the balance has been settled.',
  'pay.pendingHelp': 'Owed in full.',
  'pay.inAdvanceHelp': 'The farmer owes the cooperative.',
  'pay.paidHelp': 'Account settled.',
  /* accounts */
  'auth.createAccount': 'Create account',
  'auth.yourName': 'Your name',
  'auth.coopHelp': 'Shown across the app. You can change it later.',
  'auth.emailHelp': 'Used to sign in, and to recover your PIN.',
  'auth.choosePin': 'Choose a PIN',
  'auth.pinHelp': '4 to 8 digits. Avoid 1234 or repeated digits.',
  'auth.confirmPin': 'Confirm PIN',
  'auth.pinMismatch': 'The two PINs do not match.',
  'auth.blankStart': 'Your account starts completely empty: no farmers, no harvests, no money. You add your own, and only you can see them.',
  'auth.forgotPin': 'Forgotten your PIN?',
  'auth.resetTitle': 'Reset your PIN',
  'auth.resetSub': 'Enter the email on the account and the recovery code you saved when you signed up.',
  'auth.recoveryCode': 'Recovery code',
  'auth.recoveryCodeHelp': 'The XXXX-XXXX-XXXX code shown when you created the account.',
  'auth.newPin': 'New PIN',
  'auth.setNewPin': 'Set new PIN',
  'auth.backToSignIn': 'Back to sign in',
  'auth.emailDeliveryNote': 'Emailing a reset link needs a mail server, and this version runs entirely on your device with none. Recovery uses the code you saved instead of an email. Connect it to a server and the same flow sends that code to your address.',
  'auth.saveCodeTitle': 'Save your recovery code',
  'auth.saveCodeSub': 'This is the only way back into your account if you forget your PIN.',
  'auth.copyCode': 'Copy code',
  'auth.codeCopied': 'Recovery code copied',
  'auth.copyFailed': 'Could not copy. Write it down instead',
  'auth.codeOnceWarning': 'Shown once, now. It is stored only as a fingerprint, so nobody, including this app, can show it to you again. Write it somewhere safe before continuing.',
  'auth.confirmSaved': 'I have written down or copied this code somewhere safe.',
  'auth.continue': 'Continue',
  'auth.demoTitle': 'Just want to look around?',
  'auth.demoBody': 'Open the demo cooperative: 26 farmers, real harvests, payments and a full ledger, already filled in.',
  'auth.demoButton': 'Open the demo cooperative',
  'auth.demoBanner': 'You are in the demo cooperative. The data here is generated for illustration. Create your own account to start an empty book.',
  'auth.emptyFarmers': 'No farmers yet',
  'auth.emptyFarmersHelp': 'Add your first farmer to begin. Everything else (activities, harvests, payments, the ledger) follows from the register.',
  'login.email': 'Email',
  'login.pin': 'PIN',
  'login.showPin': 'Show PIN',
  'login.hidePin': 'Hide PIN',
  'login.badCredentials': 'That email and PIN do not match an account.',
  'login.demoHint': 'Demo sign-in: {email} · PIN {pin}',
  'login.signedIn': 'Signed in as {name}',
  'login.signInAs': 'Sign in as',
  'login.pinHelp': 'Demo PIN for this account: {pin}',

  'screen.overview.greeting': 'Good day, {name}',
  'screen.overview.attention': 'Needs your attention',
  'screen.overview.moneyPosition': 'Money position',
  'screen.farmers.title': 'Farmers',
  'screen.farmers.sub': 'Every farmer, what they are doing, what they delivered and what they are owed.',
  'screen.activities.title': 'All activities',
  'screen.production.title': 'Harvests',
  'screen.payments.outstanding': 'Outstanding balances',
  'screen.ledger.title': 'Farmer ledgers',
  'screen.reports.title': 'Reports',
  'screen.settings.title': 'Settings',

  /* metrics */
  'metric.totalFarmers': 'Total farmers',
  'metric.currentlyFarming': 'Currently farming',
  'metric.haveHarvested': 'Have harvested',
  'metric.produceCollected': 'Produce collected',
  'metric.totalEarned': 'Total earned by farmers',
  'metric.totalPaid': 'Total paid out',
  'metric.outstanding': 'Outstanding',
  'metric.advances': 'Advances outstanding',

  /* settings */
  'settings.language': 'Language',
  'settings.languageHelp': 'Applies to the interface. Names, villages and notes stay exactly as they were entered.',
  'settings.appearance': 'Appearance',
  'settings.theme': 'Theme',
  'settings.theme.light': 'Light',
  'settings.theme.dark': 'Dark',
  'settings.theme.system': 'Match device',
  'settings.themeHelp': 'Light is easier to read in sunlight; dark is easier at night and uses less battery on some phones.',
  'settings.storage': 'Storage and offline',
  'settings.roles': 'Roles and permissions',
  'settings.resetData': 'Reset data',

  /* misc */
  'misc.online': 'Online',
  'misc.offline': 'Offline, still working',
  'misc.notifications': 'Notifications',
  'misc.rows': 'rows',
  'misc.row': 'row',
  'misc.selected': 'selected',
  'misc.perPage': 'per page',
  'misc.next': 'Next',
  'misc.prev': 'Prev',
  'misc.of': 'of',
  'misc.settled': 'Settled',
  'misc.none': 'None',
  'misc.noResults': 'No rows match your search',
  'misc.noResultsHelp': 'Try a shorter search, or clear the filters above.',
  'misc.you': 'You',
  /* the + flow */
  'composer.pickTitle': 'What would you like to record?',
  'composer.pickSub': 'Pick the activity that happened. The form adapts to what you choose.',
  'composer.searchTypes': 'Search activities: harvest, payment, planting…',
  'composer.notYourRole': 'Your role cannot record this',
  'composer.postsLedger': 'Posts to ledger',
  'composer.yourType': 'Added by your cooperative',
  'composer.noMatch': 'No activity matches “{query}”. Try a shorter word, or add it as a new activity type.',
  'composer.newTypeHint': 'Add one this cooperative uses',
  'composer.newTypeSub': 'It joins the list for everyone in this cooperative, on this device.',
  'composer.typeCreated': '“{label}” added',
  'composer.typeName': 'Activity name',
  'composer.typeNameHelp': 'What your officers would call it in the field.',
  'composer.typeCategoryHelp': 'Decides where it appears in this list and in the Activities screen.',
  'composer.typeDescHelp': 'Optional one-line explanation shown under the name.',
  'composer.typeFields': 'What should it ask for?',
  'composer.typeFieldsHelp': 'Farmer, date and notes are always included. Add anything else you need.',

  /* dashboard detail */
  'dash.sub': '{farmers} farmers on the register · {deliveries} deliveries recorded this season',
  'dash.loading': 'Loading the cooperative position…',
  'dash.payFarmers': '{count} farmers awaiting payment · {amount} RWF',
  'dash.verifyHarvests': 'Verify {count} harvest records',
  'dash.approvePayments': 'Approve {count} pending payments',
  'dash.batchLeft': '{code} · {amount} RWF left',
  'dash.activeThisSeason': '{count} active this season',
  'dash.fieldWork': 'Field work under way, no delivery yet',
  'dash.deliveries': '{count} deliveries',
  'dash.kgWeight': 'KG, weight-based crops',
  'dash.rwfCredits': 'RWF, all credits posted',
  'dash.rwfSettled': 'RWF, settled payments',
  'dash.rwfOwedTo': 'RWF owed to {count} farmers',
  'dash.rwfRecover': 'RWF to recover from deliveries',
  'dash.settledOf': '{paid} of {total} settled',
  'dash.outstandingHint': 'Folded from the ledger, not a stored figure. It changes only when a transaction is posted.',
  'dash.paidToFarmers': 'Paid to farmers',
  'dash.stillOwed': 'Still owed',
  'chart.harvestByCrop': 'Harvest by crop',
  'chart.harvestByCropSub': 'Quantity received, weight-based crops in KG',
  'chart.produceOverTime': 'Produce over time',
  'chart.produceOverTimeSub': 'Monthly intake in KG',
  'chart.earnedPaid': 'Earned against paid',
  'chart.earnedPaidSub': 'Both in RWF, on one scale',
  'chart.earnedBy': 'Earned by farmers',
  'chart.paidOut': 'Paid out',
  'chart.byStatus': 'Farmers by status',
  'chart.byStatusSub': 'Where every farmer stands right now',
  'chart.byDistrict': 'Production by district',
  'chart.byDistrictSub': 'KG received',
  'chart.byCoop': 'Value by cooperative',
  'chart.byCoopSub': 'RWF of produce received',
  'chart.completion': 'Activity completion',
  'chart.completionSub': 'Recorded activities that are done or verified',
  'chart.completionLabel': '{done} of {total} complete',
  'chart.completionCaption': 'Planned and in-progress work makes up the rest.',
  'chart.owedMost': 'Owed the most',
  'chart.owedMostSub': 'Top balances waiting to be settled',
  'chart.allSettled': 'Every farmer is settled',
  'chart.allSettledSub': 'Nothing is currently owed.',
  'misc.all': 'All',
  'composer.typeNoMoney': 'Activities you add record what happened; they cannot move money. Payments, advances, bonuses and deductions stay with the built-in financial activities, which have reviewed accounting rules behind them.',
};

/* ---------------------------------------------------------- Kinyarwanda */

export const RW: Dict = {
  'app.name': 'Smart Agri-Ledger',
  'app.tagline': 'Imirimo y’abahinzi, isarura n’ubwishyu',
  'app.allCoops': 'Koperative zose',
  'app.loading': 'Turimo gufungura koperative yawe…',
  'app.offlineNote': 'Byose bikora nta interineti. Amakuru abikwa kuri iyi telefone, akaguma aho n’iyo umuyoboro wabuze.',

  'nav.overview': 'Incamake',
  'nav.farmers': 'Abahinzi',
  'nav.farmers.all': 'Abahinzi bose',
  'nav.farmers.active': 'Abahinzi bakora',
  'nav.farmers.groups': 'Amatsinda y’abahinzi',
  'nav.activities': 'Ibikorwa',
  'nav.activities.all': 'Ibikorwa byose',
  'nav.activities.farming': 'Ubuhinzi',
  'nav.activities.harvest': 'Isarura',
  'nav.activities.other': 'Ibindi bikorwa',
  'nav.production': 'Umusaruro',
  'nav.production.harvests': 'Isarura',
  'nav.production.crops': 'Imyaka',
  'nav.production.quality': 'Ubwiza',
  'nav.payments': 'Ubwishyu',
  'nav.payments.pending': 'Butegereje',
  'nav.payments.batches': 'Amatsinda y’ubwishyu',
  'nav.payments.completed': 'Bwarangiye',
  'nav.payments.outstanding': 'Imyenda isigaye',
  'nav.ledger': 'Igitabo cy’ibaruramari',
  'nav.ledger.farmers': 'Ibitabo by’abahinzi',
  'nav.ledger.transactions': 'Ibikorwa by’amafaranga',
  'nav.ledger.adjustments': 'Amakosora',
  'nav.reports': 'Raporo',
  'nav.settings': 'Igenamiterere',
  'nav.activity': 'Igikorwa',

  'action.add': 'Ongeraho',
  'action.save': 'Bika',
  'action.cancel': 'Reka',
  'action.close': 'Funga',
  'action.confirm': 'Emeza',
  'action.back': 'Subira inyuma',
  'action.export': 'Sohora',
  'action.exportCsv': 'Sohora muri CSV',
  'action.import': 'Injiza',
  'action.print': 'Capa',
  'action.search': 'Shakisha',
  'action.filters': 'Muyunguruzi',
  'action.clearAll': 'Siba byose',
  'action.reset': 'Subiza',
  'action.signIn': 'Injira',
  'action.signOut': 'Sohoka',
  'action.recordActivity': 'Andika igikorwa',
  'action.addFarmer': 'Ongeraho umuhinzi',
  'action.approve': 'Emeza',
  'action.verify': 'Genzura',
  'action.reject': 'Anga',
  'action.markPaid': 'Shyiraho ko byishyuwe',
  'action.void': 'Kuraho',
  'action.openProfile': 'Fungura umwirondoro',
  'action.showResults': 'Erekana ibisubizo',
  'action.markAllRead': 'Shyira byose nk’ibisomwe',
  'action.columns': 'Hitamo inkingi',
  'action.showAllColumns': 'Erekana inkingi zose',
  'action.newActivityType': 'Ubwoko bushya bw’igikorwa',
  'action.createActivityType': 'Kora ubwoko bw’igikorwa',

  'field.farmer': 'Umuhinzi',
  'field.farmerId': 'Nomero y’umuhinzi',
  'field.idNumber': 'Indangamuntu cyangwa nomero y\u2019umunyamuryango',
  'field.idNumberHelp': 'Indangamuntu, cyangwa nomero koperative yanyu isanzwe ikoresha kuri uyu muhinzi.',
  'field.farmerIdHelp': 'Nusiga ubusa, hakoreshwa nomero ikurikira.',
  'field.farmerIdAuto': 'Iyikora',
  'field.activity': 'Igikorwa',
  'field.crop': 'Igihingwa',
  'field.quantity': 'Ingano',
  'field.unit': 'Igipimo',
  'field.date': 'Itariki',
  'field.status': 'Imimerere',
  'field.amount': 'Amafaranga',
  'field.amountEarned': 'Amafaranga yinjiye',
  'field.paymentStatus': 'Imimerere y’ubwishyu',
  'field.balance': 'Umwenda usigaye',
  'field.notes': 'Ibisobanuro',
  'field.phone': 'Telefone',
  'field.village': 'Umudugudu',
  'field.cell': 'Akagari',
  'field.sector': 'Umurenge',
  'field.district': 'Akarere',
  'field.cooperative': 'Koperative',
  'field.season': 'Igihembwe',
  'field.firstName': 'Izina ribanza',
  'field.lastName': 'Izina ry’umuryango',
  'field.gender': 'Igitsina',
  'field.farmSize': 'Ubuso bw’umurima',
  'field.plots': 'Umubare w’ibibanza',
  'field.primaryCrop': 'Igihingwa cy’ibanze',
  'field.grade': 'Urwego rw’ubwiza',
  'field.price': 'Igiciro ku gipimo',
  'field.deductions': 'Ibyakuwemo',
  'field.gross': 'Agaciro kose',
  'field.net': 'Ayo umuhinzi ahabwa',
  'field.paid': 'Yishyuwe',
  'field.advances': 'Avansi',
  'field.reference': 'Nomero y’ubwishyu',
  'field.method': 'Uburyo',
  'field.reason': 'Impamvu',
  'field.recordedBy': 'Wanditse',
  'field.lastActivity': 'Igikorwa cya nyuma',
  'field.category': 'Icyiciro',
  'field.required': 'birakenewe',
  'field.optional': 'ntibisabwa',

  'status.active': 'Arakora',
  'status.in_progress': 'Biragenda',
  'status.harvested': 'Yasaruye',
  'status.awaiting_payment': 'Ategereje kwishyurwa',
  'status.paid': 'Yishyuwe',
  'status.disputed': 'Hari impaka',
  'status.inactive': 'Ntakora',
  'status.planned': 'Byateganyijwe',
  'status.completed': 'Byarangiye',
  'status.verified': 'Byemejwe',
  'status.cancelled': 'Byahagaritswe',
  'status.recorded': 'Byanditswe',
  'status.rejected': 'Byanzwe',
  'status.pending': 'Bitegereje',
  'status.approved': 'Byemewe',
  'status.processing': 'Biratunganywa',
  'status.draft': 'Inyandiko y’agateganyo',
  'status.pending_approval': 'Bitegereje kwemezwa',
  'status.partially_paid': 'Byishyuwe igice',

  'cat.farming': 'Ubuhinzi',
  'cat.harvest': 'Isarura',
  'cat.operations': 'Imirimo y’umurima',
  'cat.logistics': 'Gutwara ibicuruzwa',
  'cat.financial': 'Amafaranga',
  'cat.other': 'Ibindi',

  'cat.farming.blurb': 'Ibikorwa byose byo mu murima mbere y’isarura',
  'cat.harvest.blurb': 'Gukusanya, gushyira mu byiciro no kwimura umusaruro',
  'cat.operations.blurb': 'Kugenzura, gupima, ibikoresho no gutanga inyongeramusaruro',
  'cat.logistics.blurb': 'Gutora, gutanga, ububiko no kwimura ibicuruzwa',
  'cat.financial.blurb': 'Ibihindura ibyo umuhinzi agomba guhabwa',
  'cat.other.blurb': 'Amahugurwa, inama, gusura n’ibikorwa mwiyongereye',

  'role.super_admin': 'Umuyobozi mukuru',
  'role.cooperative_admin': 'Umuyobozi wa koperative',
  'role.field_officer': 'Umukozi wo mu murima',
  'role.accountant': 'Umubaruramari',
  'role.viewer': 'Ureba gusa',

  'role.super_admin.desc': 'Uburenganzira bwuzuye, harimo gucunga abakoresha no gukuraho ibyanditswe mu gitabo.',
  'role.cooperative_admin.desc': 'Ayobora koperative: abahinzi, ibikorwa, isarura no kwemeza ubwishyu.',
  'role.field_officer.desc': 'Yandika ibikorwa byo mu murima n\u2019isarura. Ntashobora gukora cyangwa kwemeza ubwishyu.',
  'role.accountant.desc': 'Ashinzwe ubwishyu, amatsinda n\u2019igitabo. Ntashobora kwandika imirimo yo mu murima.',
  'role.viewer.desc': 'Areba gusa ku mapaji yose.',
  'pay.outstandingTitle': 'Imyenda isigaye',
  'pay.outstandingSub': 'Buri muhinzi koperative igifitiye umwenda. Mubishyure uko musanzwe mubikora, hanyuma mubyandike hano. Buri mubare ubarwa uvuye mu gitabo cy\u2019uwo muhinzi, bityo uba ari wo.',
  'pay.payAFarmer': 'Andika ubwishyu',
  'pay.totalOutstanding': 'Umwenda wose usigaye',
  'pay.acrossFarmers': 'RWF ku bahinzi {count}',
  'pay.largestBalance': 'Umwenda munini',
  'pay.largestBalanceFoot': 'RWF agomba guhabwa umuhinzi umwe',
  'pay.inAdvanceCount': 'Abahinzi bafite avansi',
  'pay.inAdvanceFoot': 'Amafaranga agomba gusubizwa',
  'pay.allSettledTitle': 'Konti zose zarangiye',
  'pay.allSettledSub': 'Nta muhinzi ufitiwe umwenda kandi nta avansi isigaye.',
  'pay.pendingTitle': 'Ubwishyu butegereje',
  'pay.pendingSub': 'Ubwishyu bwemejwe cyangwa butegereje kwemezwa, ariko butaratangwa. Kubwandika hano ntibisohora amafaranga.',
  'pay.completedTitle': 'Ubwishyu bwarangiye',
  'pay.completedSub': 'Ubwishyu mwatanze kandi mwanditse. Ni bwo bwanditswe mu bitabo by\u2019abahinzi.',
  'pay.batchesTitle': 'Amatsinda y\u2019ubwishyu',
  'pay.batchesSub': 'Tegura urutonde rw\u2019abagomba kwishyurwa, ugenzure amafaranga, urusohore, hanyuma wandike buri wishyuwe.',
  'pay.newBatch': 'Itsinda rishya',
  'pay.batchFarmers': 'Abahinzi',
  'pay.batchProduce': 'Umusaruro wose',
  'pay.batchTotal': 'Amafaranga yose',
  'pay.batchPaid': 'Byishyuwe',
  'pay.batchRemaining': 'Bisigaye',
  'prod.harvestsTitle': 'Isarura',
  'prod.harvestsSub': 'Buri musaruro wakiriwe n\u2019agaciro kawo. Agaciro kose ni ingano × igiciro kandi kabarwa mu kubika. Ntabwo ari umubare wanditswe n\u2019intoki.',
  'prod.cropsTitle': 'Imyaka',
  'prod.cropsSub': 'Icyo buri gihingwa cyeze muri iki gihembwe n\u2019agaciro kacyo.',
  'prod.qualityTitle': 'Ubwiza',
  'prod.qualitySub': 'Uko umusaruro wahawe amanota, n\u2019igice cyagenzuwe.',
  'led.farmersTitle': 'Ibitabo by\u2019abahinzi',
  'led.farmersSub': 'Konti imwe kuri buri muhinzi. Kanda ku murongo urebe konti yose.',
  'led.txTitle': 'Ibikorwa by\u2019amafaranga',
  'led.txSub': 'Igitabo cyuzuye. Nta kintu gishobora guhindurwa cyangwa gusibwa. Ikosa rikurwaho hongerwaho igikorwa kibihindura, byombi bigasigara mu nyandiko.',
  'led.adjTitle': 'Amakosora',
  'led.adjSub': 'Amakosora, ibyakuweho n\u2019ibibisimbura: buri mpinduka yakozwe ku mwenda w\u2019umuhinzi, hamwe n\u2019uwayikoze.',
  'led.entriesShown': 'Ibyanditswe bigaragara',
  'led.totalCredits': 'Ibyinjiye byose',
  'led.totalDebits': 'Ibyasohotse byose',
  'led.netPosition': 'Uko bihagaze',
  'led.debit': 'Ibyasohotse',
  'led.credit': 'Ibyinjiye',
  'led.closing': 'Umwenda wa nyuma',
  'rep.title': 'Raporo',
  'rep.sub': 'Hitamo raporo, uhitemo igihe, hanyuma uyisohore muri CSV cyangwa uyicape muri PDF.',
  'prof.balanceOwed': 'Umwenda usigaye',
  'prof.totalEarned': 'Ibyinjiye byose',
  'prof.alreadyPaid': 'Byamaze kwishyurwa',
  'prof.advances': 'Avansi',
  'prof.deductions': 'Ibyakuwemo',
  'prof.allFarmers': 'Abahinzi bose',
  'prof.tab.overview': 'Incamake',
  'prof.tab.activities': 'Ibikorwa',
  'prof.tab.harvests': 'Isarura',
  'prof.tab.payments': 'Ubwishyu',
  'prof.tab.ledger': 'Igitabo',
  'prof.tab.documents': 'Inyandiko',
  'prof.farmDetails': 'Ibisobanuro by\u2019umurima',
  'prof.recentHistory': 'Amateka ya vuba',
  'pay.markPaidTitle.one': 'Kwandika ubu bwishyu ko bwishyuwe?',
  'pay.approveTitle.one': 'Kwemeza ubu bwishyu?',
  'pay.recordPaidN.one': 'Andika 1 ko byishyuwe',
  'pay.manyFarmers.one': 'umuhinzi 1',
  'pay.manyFarmers': 'abahinzi {count}',
  'dash.payFarmers.one': 'Umuhinzi 1 ategereje kwishyurwa · {amount} RWF',
  'dash.verifyHarvests.one': 'Genzura inyandiko 1 y\u2019isarura',
  'dash.approvePayments.one': 'Emeza ubwishyu 1 butegereje',
  'dash.deliveries.one': 'Ibyatanzwe 1',
  'pay.acrossFarmers.one': 'RWF ku muhinzi 1',
  'pay.batchRecordBody.one': 'Ibi byandika {amount} ku muhinzi 1 mu gitabo cye, nk\u2019amafaranga wamaze gutanga.',
  'pay.recordPaid': 'Andika ko byishyuwe',
  'pay.recordPaidN': 'Andika {count} ko byishyuwe',
  'pay.markPaidTitle': 'Kwandika ubwishyu {count} ko bwishyuwe?',
  'pay.markPaidBody': 'Emeza ko wamaze gutanga {amount} kuri {who}. Ibi byandika ubwishyu mu gitabo; ntibisohora amafaranga.',
  'pay.markPaidNote': 'Bikore gusa igihe amafaranga yamaze gutangwa. Nubyandika hakiri kare, imibare izavuga ko umuhinzi yishyuwe kandi atarishyurwa.',
  'pay.approveTitle': 'Kwemeza ubwishyu {count}?',
  'pay.approveBody': 'Kwemeza bituma {amount} yemererwa gutangwa. Nta kintu cyandikwa mu gitabo kugeza wanditse ko bwishyuwe.',
  'pay.batchRecordTitle': 'Kwandika {code} ko bwishyuwe?',
  'pay.batchRecordBody': 'Ibi byandika {amount} ku bahinzi {count} mu bitabo byabo, nk\u2019amafaranga wamaze gutanga.',
  'pay.exportToPay': 'Sohora urutonde rw\u2019ubwishyu',
  'pay.exportHint': 'Jyana uru rutonde kuri banki, ku mukozi wa mobile money, cyangwa aho mwishyurira, hanyuma ugaruke wandike ibyo watanze.',
  'pay.notAPaymentApp': 'Iyi porogaramu yandika ubwishyu, ntibutanga. Mwishyura abahinzi uko musanzwe mubikora; kubyandika hano bituma imibare yose iba ari yo.',
  'pay.nothingOwed': 'Nta mwenda',
  'pay.partPaid': 'Yishyuwe igice',
  'pay.pending': 'Ategereje',
  'pay.inAdvance': 'Yahawe avansi',
  'pay.paid': 'Yishyuwe',
  'pay.nothingOwedHelp': 'Nta mafaranga aranditswe.',
  'pay.partPaidHelp': 'Igice cy\u2019umwenda cyishyuwe.',
  'pay.pendingHelp': 'Agomba kwishyurwa byose.',
  'pay.inAdvanceHelp': 'Umuhinzi ni we ufitiye koperative umwenda.',
  'pay.paidHelp': 'Konti yarangiye.',
  'auth.createAccount': 'Fungura konti',
  'auth.yourName': 'Amazina yawe',
  'auth.coopHelp': 'Rizagaragara muri porogaramu yose. Ushobora kurihindura nyuma.',
  'auth.emailHelp': 'Ikoreshwa mu kwinjira no gusubiza PIN yawe.',
  'auth.choosePin': 'Hitamo PIN',
  'auth.pinHelp': 'Imibare 4 kugeza kuri 8. Wirinde 1234 cyangwa imibare isubiramo.',
  'auth.confirmPin': 'Emeza PIN',
  'auth.pinMismatch': 'PIN zombi ntizihuye.',
  'auth.blankStart': 'Konti yawe itangira ubusa rwose: nta bahinzi, nta isarura, nta mafaranga. Wowe ni wowe wongeramo ibyawe, kandi ni wowe wenyine ubibona.',
  'auth.forgotPin': 'Wibagiwe PIN yawe?',
  'auth.resetTitle': 'Hindura PIN yawe',
  'auth.resetSub': 'Andika imeyili ya konti n\u2019ikodi yo gusubiza wabitse igihe wafungura konti.',
  'auth.recoveryCode': 'Ikodi yo gusubiza',
  'auth.recoveryCodeHelp': 'Ikodi XXXX-XXXX-XXXX wagaragarijwe igihe wafunguraga konti.',
  'auth.newPin': 'PIN nshya',
  'auth.setNewPin': 'Shyiraho PIN nshya',
  'auth.backToSignIn': 'Subira ku kwinjira',
  'auth.emailDeliveryNote': 'Kohereza imeyili yo gusubiza bisaba seriveri y\u2019imeyili, kandi iyi verisiyo ikorera ku gikoresho cyawe gusa nta yo ifite. Ni yo mpamvu hakoreshwa ikodi wabitse aho kohereza imeyili. Nihamara guhuzwa na seriveri, iyo kodi izajya yoherezwa kuri imeyili yawe.',
  'auth.saveCodeTitle': 'Bika ikodi yawe yo gusubiza',
  'auth.saveCodeSub': 'Ni yo nzira yonyine yo kongera kwinjira muri konti yawe nutibagirwa PIN.',
  'auth.copyCode': 'Koporora ikodi',
  'auth.codeCopied': 'Ikodi yakoporowe',
  'auth.copyFailed': 'Ntibyakunze gukoporora. Yandike ku rupapuro',
  'auth.codeOnceWarning': 'Igaragara rimwe gusa, ubu. Ibikwa nk\u2019ikimenyetso gusa, bityo nta muntu, na porogaramu ubwayo, ushobora kongera kuyikwereka. Yandike ahantu hizewe mbere yo gukomeza.',
  'auth.confirmSaved': 'Naranditse cyangwa nakoporoye iyi kodi ahantu hizewe.',
  'auth.continue': 'Komeza',
  'auth.demoTitle': 'Urashaka kubanza kureba gusa?',
  'auth.demoBody': 'Fungura koperative y\u2019icyitegererezo: abahinzi 26, isarura, ubwishyu n\u2019igitabo cyuzuye.',
  'auth.demoButton': 'Fungura koperative y\u2019icyitegererezo',
  'auth.demoBanner': 'Uri muri koperative y\u2019icyitegererezo. Aya makuru ni ay\u2019icyitegererezo. Fungura konti yawe kugira ngo utangire igitabo gishya.',
  'auth.emptyFarmers': 'Nta muhinzi urahari',
  'auth.emptyFarmersHelp': 'Ongeraho umuhinzi wa mbere kugira ngo utangire. Ibindi byose (ibikorwa, isarura, ubwishyu n\u2019igitabo) bikurikira urutonde rw\u2019abahinzi.',
  'login.email': 'Imeyili',
  'login.pin': 'PIN',
  'login.showPin': 'Erekana PIN',
  'login.hidePin': 'Hisha PIN',
  'login.badCredentials': 'Iyo meyili na PIN ntibihuye na konti.',
  'login.demoHint': 'Kwinjira k\u2019icyitegererezo: {email} \u00b7 PIN {pin}',
  'login.signedIn': 'Winjiye nka {name}',
  'login.signInAs': 'Injira nka',
  'login.pinHelp': 'PIN y\u2019igerageza kuri iyi konti: {pin}',

  'screen.overview.greeting': 'Muraho, {name}',
  'screen.overview.attention': 'Bikeneye ubufasha bwawe',
  'screen.overview.moneyPosition': 'Uko amafaranga ahagaze',
  'screen.farmers.title': 'Abahinzi',
  'screen.farmers.sub': 'Buri muhinzi, ibyo arimo gukora, ibyo yatanze n’ibyo agomba kwishyurwa.',
  'screen.activities.title': 'Ibikorwa byose',
  'screen.production.title': 'Isarura',
  'screen.payments.outstanding': 'Imyenda isigaye',
  'screen.ledger.title': 'Ibitabo by’abahinzi',
  'screen.reports.title': 'Raporo',
  'screen.settings.title': 'Igenamiterere',

  'metric.totalFarmers': 'Abahinzi bose',
  'metric.currentlyFarming': 'Barimo guhinga',
  'metric.haveHarvested': 'Basaruye',
  'metric.produceCollected': 'Umusaruro wakiriwe',
  'metric.totalEarned': 'Amafaranga abahinzi binjije',
  'metric.totalPaid': 'Amafaranga yishyuwe',
  'metric.outstanding': 'Umwenda usigaye',
  'metric.advances': 'Avansi zisigaye',

  'settings.language': 'Ururimi',
  'settings.languageHelp': 'Bireba imiterere y’urubuga. Amazina, imidugudu n’ibisobanuro bisigara uko byanditswe.',
  'settings.appearance': 'Isura',
  'settings.theme': 'Isura y’urubuga',
  'settings.theme.light': 'Urumuri',
  'settings.theme.dark': 'Umwijima',
  'settings.theme.system': 'Nk’uko telefone imeze',
  'settings.themeHelp': 'Urumuri rworohereza gusoma ku zuba; umwijima woroshya amaso nijoro kandi ukoresha bateri nke kuri zimwe za telefone.',
  'settings.storage': 'Ububiko n’ikoreshwa nta interineti',
  'settings.roles': 'Inshingano n’uburenganzira',
  'settings.resetData': 'Gusiba amakuru',

  'misc.online': 'Kuri interineti',
  'misc.offline': 'Nta interineti, birakomeza gukora',
  'misc.notifications': 'Amatangazo',
  'misc.rows': 'imirongo',
  'misc.row': 'umurongo',
  'misc.selected': 'byatoranyijwe',
  'misc.perPage': 'kuri buri paji',
  'misc.next': 'Ibikurikira',
  'misc.prev': 'Ibibanza',
  'misc.of': 'kuri',
  'misc.settled': 'Yishyuwe yose',
  'misc.none': 'Nta na kimwe',
  'misc.noResults': 'Nta murongo uhuye n’ibyo washatse',
  'misc.noResultsHelp': 'Gerageza ijambo rigufi, cyangwa usibe muyunguruzi hejuru.',
  'misc.you': 'Wowe',
  'composer.pickTitle': 'Ni iki ushaka kwandika?',
  'composer.pickSub': 'Hitamo igikorwa cyabaye. Ifishi ihinduka ukurikije icyo watoranyije.',
  'composer.searchTypes': 'Shakisha ibikorwa: isarura, ubwishyu, gutera…',
  'composer.notYourRole': 'Inshingano zawe ntizikwemerera kwandika ibi',
  'composer.postsLedger': 'Bihita byandikwa mu gitabo',
  'composer.yourType': 'Cyongewemo na koperative yanyu',
  'composer.noMatch': 'Nta gikorwa gihuye na “{query}”. Gerageza ijambo rigufi, cyangwa wongereho ubwoko bushya.',
  'composer.newTypeHint': 'Ongeraho igikorwa koperative ikoresha',
  'composer.newTypeSub': 'Kizajya kigaragara ku bandi bose bakoresha iyi telefone.',
  'composer.typeCreated': '“{label}” cyongewemo',
  'composer.typeName': 'Izina ry’igikorwa',
  'composer.typeNameHelp': 'Uko abakozi bo mu murima bakita.',
  'composer.typeCategoryHelp': 'Bigena aho kizagaragara kuri uru rutonde no ku rupapuro rw’ibikorwa.',
  'composer.typeDescHelp': 'Ubusobanuro bugufi bugaragara munsi y’izina (ntibisabwa).',
  'composer.typeFields': 'Ni ibihe bisabwa kwuzuzwa?',
  'composer.typeFieldsHelp': 'Umuhinzi, itariki n’ibisobanuro bihora bihari. Ongeraho ibindi ukeneye.',

  'dash.sub': 'Abahinzi {farmers} banditswe · Ibyatanzwe {deliveries} muri iki gihembwe',
  'dash.loading': 'Turimo gukura uko amafaranga ahagaze…',
  'dash.payFarmers': 'Abahinzi {count} bategereje kwishyurwa · {amount} RWF',
  'dash.verifyHarvests': 'Genzura inyandiko {count} z’isarura',
  'dash.approvePayments': 'Emeza ubwishyu {count} butegereje',
  'dash.batchLeft': '{code} · {amount} RWF asigaye',
  'dash.activeThisSeason': '{count} bakora muri iki gihembwe',
  'dash.fieldWork': 'Barimo gukora mu murima, ntibaratanga umusaruro',
  'dash.deliveries': 'Ibyatanzwe {count}',
  'dash.kgWeight': 'KG, ibihingwa bipimwa mu buremere',
  'dash.rwfCredits': 'RWF, ibyanditswe byose',
  'dash.rwfSettled': 'RWF, ubwishyu bwarangiye',
  'dash.rwfOwedTo': 'RWF bagomba guhabwa abahinzi {count}',
  'dash.rwfRecover': 'RWF agomba gukurwa ku byatanzwe',
  'dash.settledOf': '{paid} kuri {total} byishyuwe',
  'dash.outstandingHint': 'Bibarwa bivuye mu gitabo, si umubare ubitswe. Bihinduka ari uko handitswe igikorwa.',
  'dash.paidToFarmers': 'Byishyuwe abahinzi',
  'dash.stillOwed': 'Bisigaye kwishyurwa',
  'chart.harvestByCrop': 'Umusaruro ku gihingwa',
  'chart.harvestByCropSub': 'Ingano yakiriwe, ibihingwa bipimwa muri KG',
  'chart.produceOverTime': 'Umusaruro mu gihe',
  'chart.produceOverTimeSub': 'Ibyakiriwe buri kwezi muri KG',
  'chart.earnedPaid': 'Ibyinjiye ugereranyije n’ibyishyuwe',
  'chart.earnedPaidSub': 'Byombi muri RWF, ku gipimo kimwe',
  'chart.earnedBy': 'Ibyinjiye ku bahinzi',
  'chart.paidOut': 'Ibyishyuwe',
  'chart.byStatus': 'Abahinzi ku mimerere',
  'chart.byStatusSub': 'Aho buri muhinzi ageze ubu',
  'chart.byDistrict': 'Umusaruro ku karere',
  'chart.byDistrictSub': 'KG yakiriwe',
  'chart.byCoop': 'Agaciro kuri koperative',
  'chart.byCoopSub': 'RWF y’umusaruro wakiriwe',
  'chart.completion': 'Ibikorwa byarangiye',
  'chart.completionSub': 'Ibikorwa byanditswe birangiye cyangwa byemejwe',
  'chart.completionLabel': '{done} kuri {total} byarangiye',
  'chart.completionCaption': 'Ibisigaye ni ibiteganyijwe n’ibigikomeza.',
  'chart.owedMost': 'Bagomba guhabwa byinshi',
  'chart.owedMostSub': 'Imyenda minini itegereje kwishyurwa',
  'chart.allSettled': 'Abahinzi bose bishyuwe',
  'chart.allSettledSub': 'Nta mwenda uhari ubu.',
  'misc.all': 'Byose',
  'composer.typeNoMoney': 'Ibikorwa wongeyeho byandika ibyabaye gusa; ntibishobora kwimura amafaranga. Ubwishyu, avansi, ibihembo n’ibikurwamo bikomeza gukoresha ibikorwa by’amafaranga byashyizweho, bifite amategeko y’ibaruramari yagenzuwe.',
};

/* Activity type labels, keyed by the catalog id. */
export const ACTIVITY_LABELS_RW: Dict = {
  land_preparation: 'Gutegura ubutaka',
  plowing: 'Guhinga',
  planting: 'Gutera',
  seeding: 'Kubiba imbuto',
  fertilizing: 'Gushyira ifumbire',
  irrigation: 'Kuhira',
  weeding: 'Kubagara',
  pest_control: 'Kurwanya udukoko',
  spraying: 'Gutera umuti',
  disease_treatment: 'Kuvura indwara',
  pruning: 'Gutema amashami',
  crop_monitoring: 'Gukurikirana igihingwa',

  harvesting: 'Gusarura',
  collection: 'Gukusanya umusaruro',
  sorting: 'Gutandukanya',
  grading: 'Gushyira mu byiciro',
  weighing: 'Gupima',
  packaging: 'Gupakira',
  storage: 'Kubika',
  delivery: 'Gutanga umusaruro',

  farm_inspection: 'Kugenzura umurima',
  soil_testing: 'Gupima ubutaka',
  equipment_usage: 'Gukoresha ibikoresho',
  equipment_maintenance: 'Gusana ibikoresho',
  input_distribution: 'Gutanga inyongeramusaruro',
  seed_distribution: 'Gutanga imbuto',
  fertilizer_distribution: 'Gutanga ifumbire',
  pesticide_distribution: 'Gutanga imiti',

  produce_pickup: 'Gutora umusaruro',
  produce_delivery: 'Gushyira umusaruro aho ugiye',
  warehouse_intake: 'Kwakira mu bubiko',
  transportation: 'Gutwara',
  inventory_movement: 'Kwimura ibicuruzwa',

  farmer_payment: 'Kwishyura umuhinzi',
  advance_payment: 'Avansi',
  bonus: 'Igihembo',
  deduction: 'Ikuwemo',
  loan: 'Inguzanyo',
  reimbursement: 'Kwishyura ibyakoreshejwe',
  adjustment: 'Ikosora',

  training: 'Amahugurwa',
  meeting: 'Inama',
  field_visit: 'Gusura umurima',
  contract_signing: 'Gusinya amasezerano',
  quality_inspection: 'Kugenzura ubwiza',
  custom: 'Igikorwa cyihariye',
};


/**
 * Labels for the dynamic activity-form fields, keyed by the field key in the
 * activity catalog. Anything not listed keeps the English label from the
 * catalog, which is why a new field never renders blank.
 */
export const FIELD_LABELS_RW: Dict = {
  quantity: 'Ingano',
  unit: 'Igipimo',
  grade: 'Urwego rw\u2019ubwiza',
  pricePerUnit: 'Igiciro ku gipimo',
  deductions: 'Ibyakuwemo',
  grossValue: 'Agaciro kose',
  amount: 'Amafaranga',
  method: 'Uburyo bwo kwishyura',
  reference: 'Nomero y\u2019ubwishyu',
  reason: 'Impamvu',
  notes: 'Ibisobanuro',
  attachment: 'Ifoto cyangwa inyandiko',
  areaHa: 'Ubuso bwakozweho',
  seedQuantity: 'Ingano y\u2019imbuto',
  seedVariety: 'Ubwoko bw\u2019imbuto',
  season: 'Igihembwe',
  inputName: 'Icyakoreshejwe',
  inputQuantity: 'Ingano yakoreshejwe',
  hours: 'Igihe (amasaha)',
  people: 'Abantu babigizemo uruhare',
  labourers: 'Abakozi',
  location: 'Ahantu',
  destination: 'Aho bigiye',
  from: 'Aho bivuye',
  vehicle: 'Ikinyabiziga',
  driver: 'Umushoferi',
  cost: 'Ikiguzi',
  value: 'Agaciro',
  condition: 'Uko igihingwa gihagaze',
  expectedYield: 'Umusaruro witezwe',
  pest: 'Udukoko twabonetse',
  disease: 'Indwara',
  treatment: 'Umuti watanzwe',
  topic: 'Insanganyamatsiko',
  trainer: 'Umutoza',
  attendees: 'Abitabiriye',
  officer: 'Umukozi',
  purpose: 'Intego',
  inspector: 'Ugenzura',
  outcome: 'Icyavuyemo',
  method_: 'Uburyo',
  packages: 'Amapaki',
  packageType: 'Ubwoko bw\u2019ipaki',
  rejected: 'Ibyanzwe',
  scaleId: 'Iminzani',
  waterSource: 'Aho amazi ava',
  ph: 'pH y\u2019ubutaka',
  organicMatter: 'Ibinyabuzima mu butaka',
  recommendation: 'Inama zatanzwe',
  equipment: 'Ibikoresho',
  onCredit: 'Byatanzwe ku nguzanyo',
  dueDate: 'Itariki yo kwishyura',
  direction: 'Icyerekezo',
  contractRef: 'Nomero y\u2019amasezerano',
  committedQuantity: 'Ingano yemeranyijweho',
  title: 'Izina ry\u2019igikorwa',
};


/**
 * Crop names for the crops this app ships with, keyed by crop id. A crop the
 * cooperative adds itself keeps the name they typed, translating a name
 * somebody chose would be rewriting their data.
 */
export const CROP_NAMES_RW: Dict = {
  crop_maize: 'Ibigori',
  crop_beans: 'Ibishyimbo',
  crop_irish: 'Ibirayi',
  crop_coffee: 'Ikawa',
  crop_rice: 'Umuceri',
  crop_cassava: 'Imyumbati',
  crop_banana: 'Ibitoki',
  crop_soy: 'Soya',
};

export const DICTS: Record<Lang, Dict> = { en: EN, rw: RW };
